/**
 * Where an invitation link lands.
 *
 * This is the first screen a prospective customer sees of the product, reached
 * from an email, by someone who has agreed to spend their own time helping. It
 * gets the care that deserves: their name, their school, what they are being
 * asked to do, and one field.
 *
 * It renders outside the application shell. There is no session yet, no
 * navigation that would work, and nothing above this page to go back to.
 *
 * Every way the link can fail — expired, already used, superseded, revoked —
 * is a separate message with a separate next step. Collapsing them into
 * "invalid link" is what generates support mail, and the four need four
 * different actions: ask for a new one, sign in instead, check your inbox for
 * a newer email, or contact us.
 */

import { useCallback, useEffect, useId, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError, unreachableMessage, type JoinPreview } from '@/lib/api'
import { useSession } from '@/lib/session'
import { SchoolMark } from '@/components/SchoolMark'
import { PROFESSIONAL_ROLE_LABELS } from '@/lib/feedbackTypes'
import '@/features/auth/login.css'
import './join.css'

const MIN_PASSWORD = 12

const BENEFITS = [
  'Explore Cornerstone Education at your own pace',
  'Test the workflows your school actually runs',
  'Tell us what you love — and what you do not',
  'Suggest the features your school needs',
  'Report anything broken, straight to the team',
  'Vote on ideas from other educators',
]

export function JoinPage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { refresh } = useSession()

  const [preview, setPreview] = useState<JoinPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [reveal, setReveal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const passwordId = useId()
  const confirmId = useId()

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setPreview(await api.joinPreview(token, signal))
      } catch (caught) {
        if (signal?.aborted) return
        // The API answers a dead link with a body explaining which kind of
        // dead, and that body is more useful than anything written here.
        setPreview(
          caught instanceof ApiError
            ? {
                ok: false,
                reason: (caught.details as { reason?: string })?.reason,
                message: caught.message,
              }
            : { ok: false, message: unreachableMessage() },
        )
      } finally {
        setLoading(false)
      }
    },
    [token],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD
  const mismatch = confirm.length > 0 && password !== confirm
  const ready = password.length >= MIN_PASSWORD && password === confirm && agreed

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!ready) return

    setSubmitting(true)
    setProblem(null)
    try {
      await api.join({ token, password, agreed })
      // Signed in by the same call. Asking somebody to accept an invitation
      // and then find a sign-in page is where a join flow loses people.
      await refresh()
      navigate('/feedback', { replace: true })
    } catch (caught) {
      setProblem(
        caught instanceof ApiError
          ? caught.fieldErrors.password ?? caught.fieldErrors.agreed ?? caught.message
          : unreachableMessage(),
      )
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="join">
        <div className="join__panel join__panel--centred">
          <p className="join__loading">Checking your invitation…</p>
        </div>
      </div>
    )
  }

  if (!preview?.ok) {
    return (
      <div className="join">
        <main className="join__panel join__panel--centred">
          <Brand />
          <h1 className="join__heading">This invitation cannot be used</h1>
          <p className="join__subtitle">{preview?.message ?? 'This invitation link is not valid.'}</p>
          <div className="join__actions">
            {preview?.reason === 'already_accepted' ? (
              <a className="button button--primary" href="/">
                Sign in
              </a>
            ) : (
              <a className="button button--secondary" href="mailto:support@cornerstonetech.online">
                Contact Cornerstone
              </a>
            )}
          </div>
        </main>
      </div>
    )
  }

  if (preview.hasAccount) {
    return (
      <div className="join">
        <main className="join__panel join__panel--centred">
          <Brand />
          <h1 className="join__heading">Welcome back, {preview.firstName}</h1>
          <p className="join__subtitle">
            You already have a Cornerstone account for {preview.email}. Sign in and your focus group access will be
            waiting.
          </p>
          <div className="join__actions">
            <a className="button button--primary" href="/">
              Sign in to accept
            </a>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="join">
      <main className="join__panel" aria-labelledby="join-heading">
        <Brand />

        <p className="join__eyebrow">Focus Group Invitation</p>
        <h1 className="join__heading" id="join-heading">
          Welcome, {preview.firstName}
        </h1>
        <p className="join__subtitle">
          You have been invited to help shape Cornerstone Education before its wider release.
        </p>

        <dl className="join__facts">
          {preview.organisation && (
            <div>
              <dt>School</dt>
              <dd>{preview.organisation}</dd>
            </div>
          )}
          {preview.professionalRole && (
            <div>
              <dt>Role</dt>
              <dd>{PROFESSIONAL_ROLE_LABELS[preview.professionalRole] ?? preview.professionalRole}</dd>
            </div>
          )}
          {preview.cohortName && (
            <div>
              <dt>Group</dt>
              <dd>{preview.cohortName}</dd>
            </div>
          )}
        </dl>

        <form className="login__form" onSubmit={onSubmit} noValidate>
          {problem && (
            <p className="login__message login__message--error" role="alert">
              {problem}
            </p>
          )}

          <div className="field">
            <label className="field__label" htmlFor={passwordId}>
              Choose a password
            </label>
            <div className="field__control">
              <input
                id={passwordId}
                type={reveal ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={tooShort || undefined}
                required
              />
              <button className="field__reveal" type="button" onClick={() => setReveal((shown) => !shown)}>
                {reveal ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="field__hint">
              {tooShort
                ? `${MIN_PASSWORD - password.length} more character${MIN_PASSWORD - password.length === 1 ? '' : 's'} needed.`
                : `At least ${MIN_PASSWORD} characters. A phrase you can remember beats a short password with symbols in it.`}
            </p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor={confirmId}>
              Confirm password
            </label>
            <div className="field__control">
              <input
                id={confirmId}
                type={reveal ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                aria-invalid={mismatch || undefined}
                required
              />
            </div>
            {mismatch && <p className="field__hint field__hint--error">These do not match.</p>}
          </div>

          <label className="join__agree">
            <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
            <span>
              I understand this is pre-release software, that features may change, and I agree to give honest
              feedback.
            </span>
          </label>

          <button className="button button--primary button--block" type="submit" disabled={!ready || submitting}>
            {submitting ? 'Setting up your account…' : 'Join the Focus Group'}
          </button>
        </form>

        <section className="join__benefits">
          <h2>What you will be doing</h2>
          <ul>
            {BENEFITS.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </section>

        <p className="join__note">
          Qualified participants receive Cornerstone Education free for a year, and are entered into a drawing where
          ten receive it free for life.
        </p>
      </main>

      <aside className="join__aside" aria-hidden="true">
        <div className="join__aside-inner">
          <p className="join__aside-eyebrow">Built for schools. Designed with educators.</p>
          <h2 className="join__aside-heading">
            <span>Your experience</span>
            <span>shapes what gets built.</span>
          </h2>
          <p className="join__aside-body">
            Every piece of feedback goes straight to the people building Cornerstone Education. You will see what
            changed because of it.
          </p>
        </div>
      </aside>
    </div>
  )
}

function Brand() {
  return (
    <header className="login__brand">
      <SchoolMark className="login__mark" />
      <div className="login__wordmark">
        <span className="login__wordmark-name">
          Cornerstone <strong>Education</strong>
        </span>
        <span className="login__wordmark-tagline">People · Progress · Brighter Futures</span>
      </div>
    </header>
  )
}
